import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import QuickFilterButton from "../../../common/components/functional/quickFilterButton/QuickFilterButton";
import { selectTeamRecruitmentArea } from "../../settings/recruitment/recruitmentsSlice";
import { TalentStatus } from "../../talents/types/common";
import DashboardHeaderRow from "../DashboardHeaderRow";
import {
  setFiltersToLocalStorage,
  setQuickFilterToLocalStorage,
} from "./helpers";
import { TalentQuickFilterButton } from "./types";

const TALENT_LIST_LINK = "/home/talents";

const DashboardTalents = () => {
  const { t } = useTranslation("dashboard");

  const { user } = useAppSelector((state) => state.user);
  const { counts } = useAppSelector(
    (state) => state.dashboard.talentCounts,
  );

  const teamRecruitmentAreas = useAppSelector(selectTeamRecruitmentArea);
  const ownNewTalentStatuses = [
    TalentStatus.INCOMPLETE,
    TalentStatus.NEW,
    TalentStatus.INTERVIEW_PENDING,
    TalentStatus.INTERVIEW,
  ];
  const teamNewTalentStatuses = [TalentStatus.INCOMPLETE, TalentStatus.NEW];

  const quickFilterConfigFilters: Record<TalentQuickFilterButton, any[]> = {
    [TalentQuickFilterButton.OWN_NEW]: [
      {
        name: "status",
        value: ownNewTalentStatuses,
        displayName: "Status",
      },
      {
        name: "hrRepresentative",
        value: [user?.id],
        displayName: t("talentProfile:myHr"),
        displayValue: `${user?.firstName} ${user?.lastName}`,
      },
    ],

    [TalentQuickFilterButton.TEAM_NEW]: [
      {
        name: "status",
        value: teamNewTalentStatuses,
        displayName: "Status",
      },
      {
        name: "areas",
        value: teamRecruitmentAreas.map(
          (recruitmentArea) => recruitmentArea.id,
        ),
        displayValue: teamRecruitmentAreas.map(
          (recruitmentArea) => recruitmentArea.name,
        ),
        displayName: t("common:area"),
      },
      {
        name: "hrRepresentative",
        value: ["noHrRep"],
        displayValue: t("talentProfile:noHrRep"),
        displayName: t("talentProfile:myHr"),
      },
    ],
    [TalentQuickFilterButton.OWN_ENDING_SOON]: [
      {
        name: "orderQueues",
        value: ["own"],
        displayName: t("order:team"),
        displayValue: "own",
      },
      {
        name: "hrRepresentative",
        value: [user?.id],
        displayName: t("talentProfile:myHr"),
        displayValue: `${user?.firstName} ${user?.lastName}`,
      },
    ],

    [TalentQuickFilterButton.TEAM_ENDING_SOON]: [
      {
        name: "orderQueues",
        value: ["own"],
        displayName: t("order:team"),
        displayValue: "own",
      },
    ],
    [TalentQuickFilterButton.EMPTY]: [],
  };

  const handleClick = (quickFilterButtonType: TalentQuickFilterButton) => {
    setFiltersToLocalStorage(quickFilterConfigFilters[quickFilterButtonType]);
    setQuickFilterToLocalStorage(quickFilterButtonType);
  };

  const quickFilterConfigs = {
    [TalentQuickFilterButton.OWN_NEW]: {
      count: counts?.talentsAssignedToMeCount,
      text: t("talents.ownNew"),
      action: () => handleClick(TalentQuickFilterButton.OWN_NEW),
      link: TALENT_LIST_LINK,
    },
    [TalentQuickFilterButton.TEAM_NEW]: {
      count: counts?.teamsNewTalentCount,
      text: t("talents.teamsNew"),
      action: () => handleClick(TalentQuickFilterButton.TEAM_NEW),
      link: TALENT_LIST_LINK,
    },
    [TalentQuickFilterButton.OWN_ENDING_SOON]: {
      count: counts?.talEmpEndingSoonAssignedToMeCount,
      text: t("talents.ownEndingSoon"),
      action: () => handleClick(TalentQuickFilterButton.OWN_ENDING_SOON),
      link: TALENT_LIST_LINK,
    },
    [TalentQuickFilterButton.TEAM_ENDING_SOON]: {
      count: counts?.talEmpEndingSoonAssignedToTeamCount,
      text: t("talents.teamsEndingSoon"),
      action: () => handleClick(TalentQuickFilterButton.TEAM_ENDING_SOON),
      link: TALENT_LIST_LINK,
    },
  };

  return (
    <>
      <DashboardHeaderRow title={t("talents.title")} />
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#fff",
          borderRadius: "8px",
          minHeight: "96px",
          width: "100%",
          justifyContent: "center",
          padding: "0px 8px",
        }}
      >
        <Box
          display="flex"
          width="50%"
          justifyContent="space-around"
          alignItems="center"
          padding="0px 12px"
          gap="16px"
        >
          <QuickFilterButton
            {...quickFilterConfigs[TalentQuickFilterButton.OWN_NEW]}
          />
          <QuickFilterButton
            {...quickFilterConfigs[TalentQuickFilterButton.TEAM_NEW]}
          />
        </Box>
        <Box minWidth="1px" width="1px" display="flex" bgcolor="#000A441C" />
        <Box
          display="flex"
          justifyContent="space-around"
          alignItems="center"
          width="50%"
          padding="0px 12px"
          gap="16px"
        >
          <QuickFilterButton
            {...quickFilterConfigs[TalentQuickFilterButton.OWN_ENDING_SOON]}
          />
          <QuickFilterButton
            {...quickFilterConfigs[TalentQuickFilterButton.TEAM_ENDING_SOON]}
          />
        </Box>
      </Box>
    </>
  );
};

export default DashboardTalents;
